import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { take } from 'rxjs';
import { Charge } from '../../../../models/entities/charge';
import { LegFiJwtService } from '../../../../services/auth/legfi-jwt.service';
import { JwtLegFiClaims } from '../../../../services/auth/jwt-legfi-claims.model';
import { ChargesService } from '../../../../services/charges/charges.service';
import { MatLegacyDialog as MatDialog, MatLegacyDialogModule } from '@angular/material/legacy-dialog';
import { EntityHistoryMenuItemComponent } from '../../../v2-layout/shared/entity-history/entity-history-menu-item.component';
import { PaymentGroupsService } from '../../../../services/payment/payment-groups.service';
import { GrowlerService } from '../../../shared/growler/growler.service';
import { ChargeDetailPopupComponent, ChargeDetailPopupInput } from '../charge-detail-popup/charge-detail-popup.component';
import { ConfirmActionDialogComponent, ConfirmActionDialogInput } from '../confirm-action-dialog/confirm-action-dialog.component';
import { GenericDialogResult } from '../generic-mat-dialog/generic-dialog-result';
import { ArchiveService } from '../../../../services/archive.service';
import { ValidationMessageService } from '../../../../services/validation-message.service';
import { ConfirmWithInputDialogComponent, ConfirmWithInputDialogInput, ConfirmWithInputDialogResult } from '../confirm-with-input-dialog/confirm-with-input-dialog.component';
import { CsvExportService } from '../../../../services/csv-export/csv-export.service';
import { SharedModule } from '../../../shared/shared.module';
import { MatLegacyMenuModule as MatMenuModule } from '@angular/material/legacy-menu';
import { MatIconModule } from '@angular/material/icon';
import { MatLegacyButtonModule as MatButtonModule } from '@angular/material/legacy-button';
import { SuperStarComponent } from '../super-star/super-star.component';
import { PaymentPlanDialogComponent } from '../payment-plan-dialog/payment-plan-dialog.component';
import { MatDialogSizes } from '../../../../enums/mat-dialog-sizes.enum';
import { ChargeStatusPipe } from '../../../../pipes/format/charge-status.pipe';

/**
 * Provides context-aware action menu for individual charges with operations that depend on charge state.
 *
 * Operations available vary based on charge status (unpaid/paid/forgiven), user permissions,
 * and charge attributes (has late fees, early discounts, payment plans). All destructive
 * operations require confirmation dialogs with optional reason tracking for audit purposes.
 */
@Component({
    selector: 'lf-single-charge-options',
    templateUrl: './single-charge-options.component.html',
    standalone: true,
    imports: [
        SharedModule,
        MatMenuModule,
        MatIconModule,
        MatButtonModule,
        MatLegacyDialogModule,
        SuperStarComponent,
        ChargeStatusPipe,
        EntityHistoryMenuItemComponent,
    ],
})
export class SingleChargeOptionsComponent implements OnInit
{

    @Input() charge: Charge;
    @Input() context: string = 'unpaid';

    @Output() reloadCharges: EventEmitter<void> = new EventEmitter<void>();

    jwt: JwtLegFiClaims;
    hasWriteAccess = false;
    hasPaymentsWrite = false;

    constructor(
            private _archiveService: ArchiveService,
            private _chargesService: ChargesService,
            private _csvExportService: CsvExportService,
            private _dialog: MatDialog,
            private _paymentGroupsService: PaymentGroupsService,
            private _growler: GrowlerService,
            private _validationMessageService: ValidationMessageService,
    ) {
    }

    ngOnInit() {
        this.jwt = LegFiJwtService.read();
        this.hasWriteAccess = LegFiJwtService.doesUserHaveModulePermission('charges', true);
        this.hasPaymentsWrite = LegFiJwtService.doesUserHaveModulePermission('payments', true);
    }

    /**
     * Attempts to apply available unit credits against an unpaid charge.
     *
     * Credits are processed immediately if available; fails silently if no credits exist.
     * Used for manual credit application when automatic processing was skipped or failed.
     */
    processCreditsForCharge(id: number) {
        this._paymentGroupsService.processUnitCreditPayment(null, this.charge.payorId, id)
                .pipe(take(1))
                .subscribe({
                    next: () => {
                        this._growler.success('Credit Processed', 'The specified charge should have been paid with credits if any were available.');
                        this.reloadCharges.emit();
                    },
                    error: () => {
                        this._growler.error('Credit Not Processed', 'No Payments created - maybe this unit has no credits?');
                    },
                });
    }

    chargeDetails() {
        this._dialog.open(ChargeDetailPopupComponent, {
            data: <ChargeDetailPopupInput>{
                chargeId: this.charge.id,
            },
        });
    }

    editCharge() {
        this._chargesService.editCharge$.next(this.charge.id);
    }

    /**
     * Forgives remaining unpaid balance without refunding any payments already made.
     *
     * Differs from deletion: preserves charge history and payments, only removes unpaid balance.
     * Requires optional reason for audit trail. Cannot be reversed once processed.
     */
    forgiveCharge() {
        this._dialog.open(ConfirmWithInputDialogComponent, {
            data: <ConfirmWithInputDialogInput>{
                title: 'Forgive Invoice Balance',
                content: `Are you sure you want to forgive the remaining balance of the selected invoice?`,
                btnContent: 'Yes, forgive it',
                label: 'Reason for removal',
                inputRequired: false,
            },
        }).afterClosed().subscribe((result: ConfirmWithInputDialogResult) => {
            if (result?.success) {
                this._chargesService.forgiveCharges([this.charge.id], result.value).pipe(take(1)).subscribe(
                        (response: any) => {
                            this._growler.success('Success', 'Unpaid balances of affected charges have been deleted.');
                            this.reloadCharges.emit();
                        },
                );
            }
        });
    }

    /**
     * Deletes charge and automatically refunds any payments as account credits.
     *
     * Full deletion differs from forgiveness: removes entire charge record and converts
     * any payments to credits. Emits deletion intent before dialog for parent component tracking.
     */
    deleteCharge() {
        this._chargesService.deleteCharges$.next([this.charge.id]);
        this._dialog.open(ConfirmWithInputDialogComponent, {
            data: <ConfirmWithInputDialogInput>{
                title: 'Delete Invoice?',
                content: `Are you sure you want to delete the selected invoice? Payments made against the balance of this invoice will be refunded by issuing the payor an account credit.`,
                btnContent: 'Yes, delete it',
                label: 'Reason for deletion',
                inputRequired: false,
            },
        }).afterClosed().subscribe((result: ConfirmWithInputDialogResult) => {
            if (result?.success) {
                this._chargesService.deleteCharges(null, [this.charge.id], result.value).pipe(take(1)).subscribe({
                    next: () => {
                        this._growler.success('Charges Processing...', 'You have 1 charge being deleted.');
                        this.reloadCharges.emit();
                    },
                    error: (response) => {
                        this._growler.error('Unable to delete charges...', this._validationMessageService.extractResponseErrorText(response)
                                || 'We were unable to delete your charges.');
                    },
                });
            }
        });
    }

    removeLateFees() {
        this._dialog.open(ConfirmActionDialogComponent, {
            data: <ConfirmActionDialogInput>{
                title: 'Remove Late Fees',
                content: `Are you sure you want to remove late fees from the selected invoice?`,
                confirmButton: 'Yes, remove them',
            },
        }).afterClosed().subscribe((result: GenericDialogResult) => {
            if (result.success) {
                const requestBody: { charge: any, chargeIds: number[] } = {
                    charge: {
                        removeLateFees: true,
                    },
                    chargeIds: [this.charge.id],
                };

                this._chargesService.updateCharges(null, requestBody).pipe(take(1)).subscribe(
                        (data: any) => {
                            this._growler.success('Success', 'You have removed the late fee(s).');
                            this.reloadCharges.emit();
                        },
                        () => {
                            this._growler.error('Oops...', 'Unable to remove late fees.');
                        },
                );
            }
        });
    }

    removeEarlyDiscount() {
        this._dialog.open(ConfirmActionDialogComponent, {
            data: <ConfirmActionDialogInput>{
                title: 'Remove Early Discount',
                content: `Are you sure you want to remove the early payment discount from the selected invoice?`,
                confirmButton: 'Yes, remove it',
            },
        }).afterClosed().subscribe((result: GenericDialogResult) => {
            if (result.success) {
                const requestBody: { charge: any, chargeIds: number[] } = {
                    charge: {
                        removeEarlyDiscounts: true,
                    },
                    chargeIds: [this.charge.id],
                };

                this._chargesService.updateCharges(null, requestBody).pipe(take(1)).subscribe({
                    next: (data: any) => {
                        this._growler.success('Success', 'You have removed early discounts.');
                        this.reloadCharges.emit();
                    },
                    error: () => {
                        this._growler.error('Oops..', 'Unable to remove early discount(s).');
                    },
                });
            }
        });
    }

    recordPayment() {
        this._chargesService.recordPaymentForCharges$.next([this.charge]);
    }

    createPaymentPlan() {
        this._dialog.open(PaymentPlanDialogComponent, {
            width: MatDialogSizes.LG,
            data: {
                data: [this.charge],
            },
        }).afterClosed().subscribe(result => {
            if (result === 'paymentPlanCreated') {
                this.reloadCharges.emit();
            }
        });
    }

    emailInvoice() {
        this._dialog.open(ConfirmActionDialogComponent, {
            data: <ConfirmActionDialogInput>{
                title: 'Email Invoice',
                content: `Are you sure you want to email an invoice for the selected invoice?`,
                confirmButton: 'Yes, email invoice',
            },
        }).afterClosed().subscribe((result: GenericDialogResult) => {
            if (result.success) {
                this._chargesService.sendInvoices([this.charge.id]).subscribe({
                    next: () => {
                        this._growler.success('Success', 'Your invoices are being sent. You will begin to see them in your communication log soon.');
                        this.reloadCharges.emit();
                    },
                    error: () => {
                        this._growler.error('Error', 'There was an error sending your invoices. Please try again.');
                    },
                });
            }
        });
    }

    downloadInvoice() {
        this._chargesService.downloadInvoice(this.charge.id).pipe(take(1)).subscribe({
            next: (response: Blob) => {
                this._csvExportService.downloadFile(response, `invoice-${this.charge.id}.pdf`, 'application/pdf');
            },
            error: () => {
                this._growler.oops('There was an problem downloading your invoice.');
            },
        });
    }

    /**
     * Reinstates previously forgiven or deleted charges back to active unpaid status.
     *
     * Reverses forgiveness operations but cannot restore deleted charges or recover refunded payments.
     * Requires optional reason for audit purposes.
     */
    reinstateCharge() {
        this._dialog.open(ConfirmWithInputDialogComponent, {
            data: <ConfirmWithInputDialogInput>{
                title: 'Reinstate Invoice',
                content: `Are you sure you want to reinstate the selected invoice?`,
                btnContent: 'Yes, reinstate it',
                label: 'Reason to reinstate',
                inputRequired: false,
            },
        }).afterClosed().subscribe((result: ConfirmWithInputDialogResult) => {
            if (result?.success) {
                this._chargesService.reinstateCharges([this.charge.id], result.value).pipe(take(1)).subscribe({
                    next: (response: any) => {
                        this._growler.success('Success', 'You have re-instated the charge.');
                        this.reloadCharges.emit();
                    },
                    error: () => {
                        this._growler.error('Oops...', 'There was an error reinstating the invoice. Please try again.');
                    },
                });
            }
        });
    }

    /**
     * Permanently archives charge, removing from all views and reports.
     *
     * Irreversible operation that maintains data integrity but hides charge from all interfaces.
     * Typically used for paid historical charges after retention period.
     */
    archiveCharge() {
        this._dialog.open(ConfirmActionDialogComponent, {
            data: <ConfirmActionDialogInput>{
                title: 'Archive Invoice',
                content: `Are you sure you want to archive the selected invoice? Archived invoices will no longer appear anywhere and cannot be restored.`,
                confirmButton: 'Yes, archive it',
            },
        }).afterClosed().subscribe((result: GenericDialogResult) => {
            if (result.success) {
                this._archiveService.archiveRecords(null, 'charge', [this.charge.id]).subscribe({
                    next: () => {
                        this._growler.success('Charges Archived', `Successfully archived charge`);
                        this.reloadCharges.emit();
                    },
                    error: () => {
                        this._growler.error('Error Archiving Charge', `Failed to archive the selected charge`);
                    },
                });
            }
        });
    }

}
